/**
 * Importar extracto (móvil): lógica pura para entender las filas de un CSV o
 * Excel de un banco español sin preguntar nada. Busca la fila de cabecera
 * (los bancos ponen títulos y datos de la cuenta encima), reconoce las
 * columnas de fecha, concepto e importe (o cargo y abono por separado) en
 * castellano, catalán o inglés y, si no hay cabecera, las deduce por el
 * contenido. Las fechas españolas son día/mes/año.
 */
import { looselyParseAmount } from '@actual-app/core/shared/util';

import { saldoFinalDeFilas } from '#components/mobile/historia/historia';

/** Qué hay en cada columna (índices en base 0). */
export type Columnas = {
  fecha: number;
  concepto: number;
  /** Importe con signo. Si es `null`, van `cargo` y `abono`. */
  importe: number | null;
  cargo: number | null;
  abono: number | null;
  /** Saldo tras cada movimiento, si el banco lo pone. */
  saldo?: number | null;
};

export type Lectura = {
  /** Fila de cabecera, o `null` si el archivo no trae nombres de columna. */
  filaCabecera: number | null;
  columnas: Columnas;
};

export type MovimientoLeido = {
  /** `YYYY-MM-DD`. */
  fecha: string;
  concepto: string;
  /** En euros, negativo si es un gasto. */
  importe: number;
  /** Identificador del banco (OFX), para no repetirlo nunca. */
  idBanco?: string;
};

function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const NOMBRES = {
  // «Fecha operación» antes que «fecha valor»: es cuando se pagó.
  fecha: [
    /^f(echa)? ?(de )?(la )?operacion$/,
    /^data (de )?(l )?operacio$/,
    /^fecha$/,
    /^data$/,
    /^date$/,
    /^fecha contable$/,
    /^data comptable$/,
    /^f contable$/,
    /^fecha/,
    /^data/,
    /date/,
  ],
  concepto: [
    /^concepto$/,
    /^concepte$/,
    /^descripcion$/,
    /^descripcio$/,
    /^description$/,
    /^movimiento$/,
    /^moviment$/,
    /^detalle$/,
    /^payee$/,
    /concepto|concepte|descripci|movimiento|moviment|detalle|beneficiario|comercio/,
  ],
  importe: [
    /^importe$/,
    /^import$/,
    /^amount$/,
    /^cantidad$/,
    /^quantitat$/,
    /^importe (eur|euros)$/,
    /^import (eur|euros)$/,
    /importe|^import\b|amount/,
  ],
  cargo: [
    /^cargo(s)?$/,
    /^debe$/,
    /^gasto(s)?$/,
    /^carrec(s)?$/,
    /^deure$/,
    /^debit$/,
  ],
  saldo: [
    /^saldo$/,
    /^saldo (disponible|contable|final|actual|eur|euros)$/,
    /^balance$/,
    /^saldo/,
  ],
  abono: [
    /^abono(s)?$/,
    /^haber$/,
    /^ingreso(s)?$/,
    /^abonament(s)?$/,
    /^haver$/,
    /^credit$/,
  ],
} as const;

type Campo = keyof typeof NOMBRES;

/** Columna cuyo nombre encaja mejor (el primer patrón que acierta gana). */
function buscar(cabecera: string[], campo: Campo, usadas: Set<number>) {
  const nombres = cabecera.map(normalizar);
  for (const patron of NOMBRES[campo]) {
    const indice = nombres.findIndex(
      (nombre, i) => !usadas.has(i) && patron.test(nombre),
    );
    if (indice !== -1) {
      return indice;
    }
  }
  return null;
}

function columnasPorNombre(cabecera: string[]): Columnas | null {
  const usadas = new Set<number>();
  const fecha = buscar(cabecera, 'fecha', usadas);
  if (fecha == null) {
    return null;
  }
  usadas.add(fecha);
  // Una segunda fecha («fecha valor») no puede ser el concepto.
  cabecera.forEach((nombre, i) => {
    if (/^(f(echa)?|data) ?valor$/.test(normalizar(nombre))) {
      usadas.add(i);
    }
  });
  const importe = buscar(cabecera, 'importe', usadas);
  if (importe != null) {
    usadas.add(importe);
  }
  const cargo = importe == null ? buscar(cabecera, 'cargo', usadas) : null;
  const abono = importe == null ? buscar(cabecera, 'abono', usadas) : null;
  if (importe == null && (cargo == null || abono == null)) {
    return null;
  }
  if (cargo != null) {
    usadas.add(cargo);
  }
  if (abono != null) {
    usadas.add(abono);
  }
  const concepto = buscar(cabecera, 'concepto', usadas);
  if (concepto == null) {
    return null;
  }
  usadas.add(concepto);
  const saldo = buscar(cabecera, 'saldo', usadas);
  return { fecha, concepto, importe, cargo, abono, saldo };
}

/** `13/10/2026`, `13-10-26`, `2026-10-13`, `13.10.2026` → `YYYY-MM-DD`. */
export function leerFecha(texto: string): string | null {
  const limpio = texto.trim();
  let m = limpio.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[ T].*)?$/);
  let anyo: number;
  let mes: number;
  let dia: number;
  if (m) {
    [anyo, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  } else {
    m = limpio.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})(?: .*)?$/);
    if (!m) {
      return null;
    }
    [dia, mes, anyo] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (anyo < 100) {
      anyo += 2000;
    }
  }
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) {
    return null;
  }
  const fecha = new Date(Date.UTC(anyo, mes - 1, dia));
  if (fecha.getUTCDate() !== dia) {
    return null;
  }
  return fecha.toISOString().slice(0, 10);
}

/** `-1.234,56 €`, `23,45`, `(12,00)`, `+15.00 EUR` → número; vacío → `null`. */
export function leerImporte(texto: string): number | null {
  const limpio = texto.replace(/€|eur(os)?/gi, '').replace(/\s+/g, '');
  if (!/\d/.test(limpio) || /[a-z]/i.test(limpio)) {
    return null;
  }
  const signo = limpio.endsWith('-') ? '-' : '';
  return looselyParseAmount(signo + limpio.replace(/^\+|-$/g, ''));
}

function proporcion(
  filas: string[][],
  col: number,
  prueba: (v: string) => boolean,
) {
  const valores = filas.map(f => f[col] ?? '').filter(v => v !== '');
  if (valores.length === 0) {
    return 0;
  }
  return valores.filter(prueba).length / valores.length;
}

/** Sin cabecera: fecha e importe por su forma; concepto, el texto más largo. */
function columnasPorContenido(filas: string[][]): Columnas | null {
  const muestra = filas.slice(0, 50);
  const ancho = Math.max(0, ...muestra.map(f => f.length));
  const indices = Array.from({ length: ancho }, (_, i) => i);
  const fecha = indices.find(
    i => proporcion(muestra, i, v => leerFecha(v) != null) >= 0.8,
  );
  if (fecha == null) {
    return null;
  }
  const numericas = indices.filter(
    i =>
      i !== fecha && proporcion(muestra, i, v => leerImporte(v) != null) >= 0.8,
  );
  // Si hay varias, la primera es el importe (después suele ir el saldo).
  const importe = numericas[0];
  if (importe == null) {
    return null;
  }
  const textos = indices.filter(
    i =>
      i !== fecha &&
      !numericas.includes(i) &&
      proporcion(muestra, i, v => leerFecha(v) == null) >= 0.8,
  );
  const largo = (i: number) =>
    muestra.reduce((suma, f) => suma + (f[i] ?? '').length, 0);
  const concepto = textos.sort((a, b) => largo(b) - largo(a))[0];
  if (concepto == null) {
    return null;
  }
  // La segunda columna de números suele ser el saldo.
  const saldo = numericas[1] ?? null;
  return { fecha, concepto, importe, cargo: null, abono: null, saldo };
}

/** Entiende las filas de un extracto, o `null` si no encuentra lo mínimo. */
export function leerColumnas(filas: string[][]): Lectura | null {
  // La cabecera está en las primeras filas; debajo tiene que haber fechas.
  for (let i = 0; i < Math.min(filas.length, 30); i++) {
    const columnas = columnasPorNombre(filas[i]);
    if (
      columnas &&
      filas.slice(i + 1).some(f => leerFecha(f[columnas.fecha] ?? '') != null)
    ) {
      return { filaCabecera: i, columnas };
    }
  }
  const columnas = columnasPorContenido(filas);
  return columnas ? { filaCabecera: null, columnas } : null;
}

function importeDeFila(fila: string[], c: Columnas): number | null {
  if (c.importe != null) {
    return leerImporte(fila[c.importe] ?? '');
  }
  const cargo = c.cargo != null ? leerImporte(fila[c.cargo] ?? '') : null;
  const abono = c.abono != null ? leerImporte(fila[c.abono] ?? '') : null;
  if (cargo == null && abono == null) {
    return null;
  }
  // El cargo resta aunque el banco lo escriba en positivo.
  return (abono ?? 0) - Math.abs(cargo ?? 0);
}

/**
 * Los movimientos de las filas, con las columnas ya leídas. Las filas que no
 * tienen fecha o importe (subtotales, saldo final, pies) se cuentan aparte.
 */
export function movimientosDeFilas(
  filas: string[][],
  lectura: Lectura,
): {
  movimientos: MovimientoLeido[];
  descartadas: number;
  /** Saldo de la fila más reciente (columna «Saldo»), si la hay. */
  saldoFinal: number | null;
} {
  const { columnas, filaCabecera } = lectura;
  const cuerpo = filas.slice(filaCabecera == null ? 0 : filaCabecera + 1);
  const movimientos: MovimientoLeido[] = [];
  const saldos: { fecha: string; saldo: number | null }[] = [];
  let descartadas = 0;
  for (const fila of cuerpo) {
    const fecha = leerFecha(fila[columnas.fecha] ?? '');
    const importe = importeDeFila(fila, columnas);
    if (fecha == null || importe == null) {
      descartadas++;
      continue;
    }
    movimientos.push({
      fecha,
      concepto: (fila[columnas.concepto] ?? '').replace(/\s+/g, ' ').trim(),
      importe,
    });
    if (columnas.saldo != null) {
      saldos.push({ fecha, saldo: leerImporte(fila[columnas.saldo] ?? '') });
    }
  }
  return { movimientos, descartadas, saldoFinal: saldoFinalDeFilas(saldos) };
}

/**
 * Filas de un CSV cuyo separador no se sabe: se queda con el que da más
 * columnas de forma estable (`;` es el habitual en España).
 */
export function elegirSeparador(
  porSeparador: Record<string, string[][]>,
): string {
  let mejor = ';';
  let mejorPuntos = -1;
  for (const [separador, filas] of Object.entries(porSeparador)) {
    const muestra = filas.slice(0, 30);
    const anchos = muestra.map(f => f.length).sort((a, b) => a - b);
    const mediana = anchos[Math.floor(anchos.length / 2)] ?? 0;
    const lectura = leerColumnas(filas);
    // Entender las columnas pesa más que tenerlas.
    const puntos = (lectura ? 100 : 0) + mediana;
    if (puntos > mejorPuntos) {
      mejor = separador;
      mejorPuntos = puntos;
    }
  }
  return mejor;
}
